import { FC } from 'react';
import { Card, CardHeader, IconButton, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import type { Cut, Extra, Trim } from '../../../types';

interface SortableItemProps {
  item: Cut | Trim | Extra;
  itemType: 'cut' | 'trim' | 'extra' | null;
  /** Pre-bound edit callback */
  onEdit?: () => void;
  /** Pre-bound remove callback */
  onRemove?: () => void;
}

/**
 * Single draggable row for a FreeSelection question's item list.
 *
 * Uses a drag-handle icon so users can reorder items within the card.
 * The card lifts (z-index + opacity) while being dragged.
 * @param root0
 * @param root0.item
 * @param root0.onEdit
 * @param root0.onRemove
 */
export const SortableItem: FC<SortableItemProps> = ({
  item,
  onEdit: _onEdit,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      sx={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 1 : 'auto',
        position: 'relative',
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
      }}
    >
      <CardHeader
        avatar={
          // Drag handle — grab here to reorder
          <IconButton
            size="small"
            sx={{
              cursor: 'grab',
              color: 'text.disabled',
              touchAction: 'none',
              '&:active': { cursor: 'grabbing' },
            }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        }
        action={
          onRemove && (
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={onRemove}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )
        }
        title={item.title || 'Unnamed'}
        slotProps={{
          title: { variant: 'body2', fontWeight: 'medium' },
          action: { sx: { alignSelf: 'center' } },
        }}
      />
    </Card>
  );
};
